import React from "react";
import { Box } from "@mui/material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";

import { RADIUS, VIOLET } from "../styles/brandTokens";
import type { AutomationCapacity } from "../types/integration";

/** Labels for automated capacities shown on the message that started the run. */
const AUTO_MODE_BADGE_LABELS: Partial<Record<AutomationCapacity, string>> = {
  3: "Auto Resolver",
};

/** Long agent names end in an ellipsis so the byline stays on one line. */
const AUTO_MODE_BADGE_MAX_WIDTH = 180;

interface AutoModeBadgeProps {
  capacity?: AutomationCapacity;
  /** The agent the run belongs to, when known. */
  agentName?: string | null;
}

/**
 * Byline chip for a message that started an automated run. Violet marks it as Theo's doing;
 * the control radius and hairline border keep it in the header toolbar's family.
 *
 * The run is named by its agent when the agent is known ("Theo Agent"), so the chip matches the
 * agent picked in the mode selector; otherwise it reads "Auto Resolver".
 */
export function AutoModeBadge({ capacity, agentName }: AutoModeBadgeProps) {
  const modeLabel = capacity ? AUTO_MODE_BADGE_LABELS[capacity] : undefined;
  if (!modeLabel) return null;
  const label = String(agentName || "").trim() || modeLabel;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        border: `1px solid ${VIOLET.tint}`,
        backgroundColor: VIOLET.wash,
        color: VIOLET.theoText,
        borderRadius: `${RADIUS.control}px`,
        px: 0.75,
        py: 0.15,
        fontFamily: "var(--theo-font-sans)",
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      <BoltOutlinedIcon aria-hidden="true" sx={{ fontSize: 15, color: VIOLET.theo, ml: -0.25 }} />
      <Box
        component="span"
        title={label === modeLabel ? undefined : `${modeLabel} as ${label}`}
        sx={{ maxWidth: AUTO_MODE_BADGE_MAX_WIDTH, overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {label}
      </Box>
    </Box>
  );
}

export default AutoModeBadge;
